// src/pages/Enseignants/Palmares/CreatePalmaresModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaCopy,
  FaEye,
  FaIdCard,
  FaInfoCircle,
  FaMedal,
  FaSave,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaTrophy,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { addPalmares } from "./../../../services/palmaresServices";
import { getAllAdherentsList } from "../../../services/adherentServices";
import { getAllEvents } from "../../../services/eventServices";

const defaultForm = () => ({
  adherent_palmares_id: "",
  event_palmares_id: "",
  date_palmares: "",
  titre_palmares: "",
  resultats_palmares: "",
  classement_palmares: "",
  observations_palmares: "",
  is_active: true,
});

const inputClass =
  "min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-violet-400 dark:focus:ring-violet-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const normalizeText = (value = "") => String(value || "").trim();

const toAbsolute = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (!cleanUrl.startsWith("/uploads")) return cleanUrl;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${cleanUrl}`;
};

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

const getAdherentPhoto = (adherent) => {
  if (!adherent) return "/img/user_avatar.jpg";

  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  return (
    toAbsolute(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar
    ) || getDefaultPhoto(adherent?.genre)
  );
};

const getAdherentName = (adherent) => {
  if (!adherent) return "Aucun adhérent sélectionné";

  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

const getEventName = (event) =>
  event?.titre ||
  event?.title ||
  event?.nom ||
  event?.name ||
  event?.event_nom ||
  "";

const getEventRawDate = (event) =>
  event?.date ||
  event?.date_event ||
  event?.jour ||
  event?.datetime ||
  event?.start ||
  null;

const getEventDateInput = (event) => {
  const rawDate = getEventRawDate(event);

  if (!rawDate) return "";

  const date = new Date(rawDate);

  if (Number.isNaN(date.getTime())) return "";

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

const formatDateFR = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("fr-FR");
};

const formatEventLabel = (event) => {
  const name = getEventName(event) || "Événement";
  const dateStr = formatDateFR(getEventRawDate(event));
  const place = event?.lieu || event?.ville || event?.location || "";

  return [name, dateStr, place].filter(Boolean).join(" — ");
};

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>

          <div>
            <h4 className="text-sm font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            {description && (
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4">{children}</div>
    </section>
  );
}

function VisibilitySwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      className={`flex w-full items-center justify-between gap-4 rounded-2xl p-4 text-left ring-1 transition ${
        checked
          ? "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
          : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:bg-slate-900"
      }`}
      title={checked ? "Affiché sur l’accueil" : "Masqué de l’accueil"}
    >
      <span className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            checked
              ? "bg-emerald-600 text-white"
              : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          }`}
        >
          {checked ? <FaEye /> : <FaTimes />}
        </span>

        <span>
          <span className="block text-sm font-bold">
            Afficher sur la page d’accueil
          </span>
          <span className="mt-0.5 block text-xs opacity-75">
            {checked
              ? "Ce palmarès sera visible publiquement."
              : "Ce palmarès restera masqué."}
          </span>
        </span>
      </span>

      <span
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
      >
        <span
          className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </span>
    </button>
  );
}

function SelectedAdherentCard({ adherent, loading }) {
  const photo = getAdherentPhoto(adherent);
  const name = getAdherentName(adherent);

  return (
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-950 dark:bg-slate-900">
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
            <FaUser className="text-xl" />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-white/70">
              Adhérent
            </p>
            <p className="text-lg font-bold">
              {adherent ? "Sélectionné" : loading ? "Chargement..." : "À choisir"}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-col items-center text-center">
          <div className="h-36 w-36 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900">
            {adherent ? (
              <img
                src={photo}
                alt={name}
                className="h-full w-full object-cover"
                draggable={false}
                onError={(event) => {
                  event.currentTarget.src = "/img/user_avatar.jpg";
                }}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-400">
                <FaUser className="text-3xl" />
                <span className="px-3 text-xs font-semibold">
                  Sélectionnez un adhérent
                </span>
              </div>
            )}
          </div>

          <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
            {name}
          </h3>

          {adherent?.statut_inscription && (
            <span className="mt-2 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
              {adherent.statut_inscription}
            </span>
          )}

          {(adherent?.saison_active || adherent?.licence_number) && (
            <div className="mt-4 grid w-full grid-cols-1 gap-2">
              {adherent?.saison_active && (
                <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Saison
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                    {adherent.saison_active}
                  </p>
                </div>
              )}

              {adherent?.licence_number && (
                <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Licence
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                    {adherent.licence_number}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function CreatePalmaresModal({ isOpen, onClose, refreshList }) {
  const dialogRef = useRef(null);

  const [form, setForm] = useState(defaultForm());

  const [adherents, setAdherents] = useState([]);
  const [loadingAdherents, setLoadingAdherents] = useState(false);

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventQuery, setEventQuery] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return;

    setForm(defaultForm());
    setEventQuery("");

    const loadAdherents = async () => {
      try {
        setLoadingAdherents(true);
        const data = await getAllAdherentsList();
        setAdherents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents", error);
        toast.error("Impossible de charger la liste des adhérents.");
      } finally {
        setLoadingAdherents(false);
      }
    };

    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const evts = await getAllEvents();
        const list = Array.isArray(evts) ? evts : [];

        list.sort((a, b) => {
          const da = new Date(getEventRawDate(a));
          const db = new Date(getEventRawDate(b));
          const va = Number.isNaN(da.getTime()) ? 0 : da.getTime();
          const vb = Number.isNaN(db.getTime()) ? 0 : db.getTime();

          return vb - va;
        });

        setEvents(list);
      } catch (error) {
        console.error("Erreur lors de la récupération des événements", error);
        toast.error("Impossible de charger la liste des événements.");
      } finally {
        setLoadingEvents(false);
      }
    };

    loadAdherents();
    loadEvents();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const root = window.document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener("keydown", onKey);

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("select[name='adherent_palmares_id']")?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKey);
      root.style.overflow = previousOverflow;
    };
  }, [isOpen, handleClose]);

  const sortedMembers = useMemo(() => {
    return [...adherents].sort((a, b) =>
      `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
        `${b.nom || ""} ${b.prenom || ""}`,
        "fr",
        { sensitivity: "base" }
      )
    );
  }, [adherents]);

  const selectedAdherent = useMemo(() => {
    if (!form.adherent_palmares_id) return null;

    return (
      sortedMembers.find(
        (adherent) =>
          String(adherent.id) === String(form.adherent_palmares_id)
      ) || null
    );
  }, [form.adherent_palmares_id, sortedMembers]);

  const selectedEvent = useMemo(() => {
    if (!form.event_palmares_id) return null;

    return (
      events.find((event) => String(event.id) === String(form.event_palmares_id)) ||
      null
    );
  }, [events, form.event_palmares_id]);

  const filteredEvents = useMemo(() => {
    const query = eventQuery.trim().toLowerCase();

    if (!query) return events;

    return events.filter((event) =>
      formatEventLabel(event).toLowerCase().includes(query)
    );
  }, [events, eventQuery]);

  const canSubmit = useMemo(() => {
    return (
      !!form.adherent_palmares_id &&
      !!form.date_palmares &&
      normalizeText(form.titre_palmares).length > 0 &&
      !submitting
    );
  }, [form.adherent_palmares_id, form.date_palmares, form.titre_palmares, submitting]);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEventChange = (event) => {
    const eventId = event.target.value;
    const evt = events.find((item) => String(item.id) === String(eventId));
    const eventDate = getEventDateInput(evt);

    setForm((previous) => ({
      ...previous,
      event_palmares_id: eventId,
      date_palmares: previous.date_palmares || eventDate || previous.date_palmares,
    }));
  };

  const copyEventToTitle = () => {
    if (!selectedEvent) {
      toast.info("Sélectionnez un événement d’abord.");
      return;
    }

    const name = getEventName(selectedEvent);

    if (!name) {
      toast.info("Événement sans nom exploitable.");
      return;
    }

    setForm((previous) => ({
      ...previous,
      titre_palmares: previous.titre_palmares?.trim()
        ? previous.titre_palmares
        : name,
      date_palmares:
        previous.date_palmares || getEventDateInput(selectedEvent) || "",
    }));

    if (!form.titre_palmares?.trim()) {
      toast.success("Titre renseigné depuis l’événement.");
    }
  };

  const removeSelectedEvent = () => {
    setForm((previous) => ({
      ...previous,
      event_palmares_id: "",
    }));
  };

  const validate = () => {
    if (!form.adherent_palmares_id) {
      toast.warn("Sélectionnez un adhérent.");
      return false;
    }

    if (!form.date_palmares) {
      toast.warn("La date est requise.");
      return false;
    }

    if (!form.titre_palmares.trim()) {
      toast.warn("Le titre est requis.");
      return false;
    }

    return true;
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!validate()) return;

      setSubmitting(true);

      try {
        const payload = {
          ...form,
          event_palmares_id: form.event_palmares_id || null,
          titre_palmares: normalizeText(form.titre_palmares),
          resultats_palmares: normalizeText(form.resultats_palmares),
          classement_palmares: normalizeText(form.classement_palmares),
          observations_palmares: String(form.observations_palmares || "").trim(),
          is_active: !!form.is_active,
        };

        await addPalmares(payload);

        toast.success("Palmarès ajouté avec succès !");

        refreshList?.();
        onClose?.();
      } catch (error) {
        console.error("Erreur lors de la création du palmarès", error);
        toast.error("Erreur lors de la création du palmarès.");
      } finally {
        setSubmitting(false);
      }
    },
    [form, onClose, refreshList]
  );

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter un palmarès"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90dvh] sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-violet-600 to-pink-600 px-4 py-5 text-white sm:px-6">
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/75">
                Palmarès club
              </p>

              <h3 className="mt-1 text-xl font-bold text-white sm:text-2xl">
                Ajouter un palmarès
              </h3>

              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-white/85">
                Sélectionnez l’adhérent, ajoutez le résultat, puis choisissez si
                le palmarès doit apparaître sur la page d’accueil.
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 disabled:opacity-60"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="palmares-modal-scroll min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <SelectedAdherentCard
                  adherent={selectedAdherent}
                  loading={loadingAdherents}
                />

                <SectionCard
                  icon={FaEye}
                  title="Visibilité"
                  description="Contrôle l’affichage du palmarès sur l’accueil."
                >
                  <VisibilitySwitch
                    checked={!!form.is_active}
                    onChange={() =>
                      setForm((previous) => ({
                        ...previous,
                        is_active: !previous.is_active,
                      }))
                    }
                  />
                </SectionCard>

                <SectionCard
                  icon={FaInfoCircle}
                  title="Résumé"
                  description="Vérification rapide avant enregistrement."
                >
                  <div className="space-y-3">
                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Titre
                      </p>
                      <p className="mt-1 line-clamp-2 text-sm font-bold text-slate-800 dark:text-white">
                        {form.titre_palmares || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Classement
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                        {form.classement_palmares || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-100 dark:bg-slate-950 dark:ring-slate-800">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Date
                      </p>
                      <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
                        {form.date_palmares
                          ? formatDateFR(form.date_palmares)
                          : "—"}
                      </p>
                    </div>
                  </div>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaUser}
                  title="Adhérent et date"
                  description="La photo de l’adhérent s’affiche automatiquement après sélection."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>
                        Adhérent <span className="text-pink-600">*</span>
                      </label>

                      <select
                        name="adherent_palmares_id"
                        value={form.adherent_palmares_id}
                        onChange={onChange}
                        className={inputClass}
                        required
                        disabled={loadingAdherents || submitting}
                      >
                        <option value="">
                          {loadingAdherents
                            ? "Chargement…"
                            : "— Sélectionnez un adhérent —"}
                        </option>

                        {!loadingAdherents &&
                          sortedMembers.map((adherent) => (
                            <option key={adherent.id} value={adherent.id}>
                              {(adherent.nom || "").toUpperCase()}{" "}
                              {adherent.prenom || ""}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>
                        Date <span className="text-pink-600">*</span>
                      </label>

                      <div className="relative">
                        <FaCalendarAlt className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="date"
                          name="date_palmares"
                          value={form.date_palmares}
                          onChange={onChange}
                          className={`${inputClass} pl-9`}
                          required
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaTrophy}
                  title="Événement associé"
                  description="Optionnel : associer ce palmarès à un événement du calendrier."
                >
                  <div className="space-y-3">
                    <div>
                      <label className={labelClass}>Rechercher un événement</label>

                      <div className="relative">
                        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                        <input
                          type="text"
                          value={eventQuery}
                          onChange={(event) => setEventQuery(event.target.value)}
                          placeholder="Nom, date, lieu…"
                          className={`${inputClass} pl-9`}
                          disabled={loadingEvents || submitting}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 lg:grid-cols-[1fr_auto]">
                      <select
                        name="event_palmares_id"
                        value={form.event_palmares_id || ""}
                        onChange={handleEventChange}
                        className={inputClass}
                        disabled={loadingEvents || submitting}
                      >
                        <option value="">
                          {loadingEvents
                            ? "Chargement…"
                            : "— Aucun événement —"}
                        </option>

                        {!loadingEvents &&
                          filteredEvents.map((event) => (
                            <option key={event.id} value={event.id}>
                              {formatEventLabel(event)}
                            </option>
                          ))}
                      </select>

                      <button
                        type="button"
                        onClick={copyEventToTitle}
                        disabled={!selectedEvent || submitting}
                        className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-900"
                      >
                        <FaCopy />
                        Copier titre/date
                      </button>
                    </div>

                    {selectedEvent && (
                      <div className="flex flex-col gap-2 rounded-2xl bg-violet-50 p-3 text-sm text-violet-800 ring-1 ring-violet-100 dark:bg-violet-900/20 dark:text-violet-200 dark:ring-violet-800/60 sm:flex-row sm:items-center sm:justify-between">
                        <span className="inline-flex items-center gap-2 font-bold">
                          <FaCheckCircle />
                          Événement sélectionné
                        </span>

                        <button
                          type="button"
                          onClick={removeSelectedEvent}
                          className="text-left text-xs font-bold underline sm:text-right"
                        >
                          Retirer l’événement
                        </button>
                      </div>
                    )}
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaMedal}
                  title="Résultat sportif"
                  description="Titre, classement et détails du résultat obtenu."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelClass}>
                        Titre de l’événement / compétition{" "}
                        <span className="text-pink-600">*</span>
                      </label>

                      <input
                        type="text"
                        name="titre_palmares"
                        value={form.titre_palmares}
                        onChange={onChange}
                        placeholder="Ex : 1er au Tournoi Régional"
                        className={inputClass}
                        required
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Résultats</label>

                      <input
                        type="text"
                        name="resultats_palmares"
                        value={form.resultats_palmares}
                        onChange={onChange}
                        placeholder="Ex : 3 combats gagnés, 1 perdu"
                        className={inputClass}
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Classement</label>

                      <input
                        type="text"
                        name="classement_palmares"
                        value={form.classement_palmares}
                        onChange={onChange}
                        placeholder="Ex : 1er, 2e, 3e"
                        className={inputClass}
                        disabled={submitting}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <div className="mb-2 flex flex-wrap gap-2">
                        {["1er", "2e", "3e", "Participation"].map((value) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() =>
                              setForm((previous) => ({
                                ...previous,
                                classement_palmares: value,
                              }))
                            }
                            disabled={submitting}
                            className={`inline-flex min-h-[38px] items-center rounded-2xl px-3 py-1.5 text-xs font-bold ring-1 transition ${
                              form.classement_palmares === value
                                ? "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-900/20 dark:text-violet-200 dark:ring-violet-800/60"
                                : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-700"
                            }`}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaIdCard}
                  title="Observations"
                  description="Notes internes ou commentaire à afficher avec le palmarès."
                >
                  <textarea
                    name="observations_palmares"
                    value={form.observations_palmares}
                    onChange={onChange}
                    placeholder="Notes, contexte, remarques…"
                    rows={5}
                    className={`${inputClass} resize-none leading-relaxed`}
                    disabled={submitting}
                  />
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <FaSave />
                    Enregistrer le palmarès
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .palmares-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }

        .palmares-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.45);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        .palmares-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dark .palmares-modal-scroll::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.35);
          border: 3px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>,
    window.document.body
  );
}